import { create } from 'zustand';
import type { ClipEdits } from '../core/clips';
import type { Framing, OutputFormat, Quality } from '../core/format';
import { MATCH_FORMATS } from '../core/timeline';

export type Step = 'start' | 'timestamps' | 'mark' | 'sync' | 'clips' | 'export';
export type Method = 'list' | 'live';

export interface VideoInfo {
  duration: number;
  width: number;
  height: number;
  fps: number | null;
  codec: string | null;
  hasAudio: boolean;
  canDecode: boolean;
}

export interface MarkClip {
  id: string;
  origin: 'mark' | 'manual';
  start: number;
  end: number;
}

export interface ExportSettings {
  padBefore: number;
  padAfter: number;
  mergeGap: number;
  format: OutputFormat;
  framing: Framing;
  cropPos: number;
  quality: Quality;
}

/** Everything saved per video file in IndexedDB. */
export interface Project {
  version: 1;
  method: Method;
  source: 'veo' | 'list';
  timeBase: 'match' | 'video';
  text: string;
  defaultLength: number;
  formatId: string;
  clockAt: Record<number, number>;
  kickoffs: Record<number, number>;
  marks: MarkClip[];
  markMode: 'range' | 'single';
  markBefore: number;
  markAfter: number;
  edits: ClipEdits;
  settings: ExportSettings;
  savedAt: number;
}

export const defaultProject = (): Project => ({
  version: 1,
  method: 'list',
  source: 'veo',
  timeBase: 'match',
  text: '',
  defaultLength: 10,
  formatId: 'adult',
  clockAt: { ...MATCH_FORMATS[0].clockAt },
  kickoffs: {},
  marks: [],
  markMode: 'range',
  markBefore: 8,
  markAfter: 2,
  edits: { bounds: {}, excluded: {}, labels: {} },
  settings: { padBefore: 1, padAfter: 1, mergeGap: 1, format: 'h', framing: 'crop', cropPos: 0.5, quality: 'orig' },
  savedAt: 0,
});

interface AppState {
  step: Step;
  file: File | null;
  url: string | null;
  fileKey: string | null;
  info: VideoInfo | null;
  probeError: string | null;
  restorable: Project | null;
  project: Project;
  /** True while an export runs: navigation is locked. */
  exporting: boolean;
  /** The Guide page sits on top of the flow, so opening it never loses work. */
  view: 'app' | 'guide';
  /** Bumped to replay the tour on the current screen. */
  tourNonce: number;
  openGuide: (section?: string) => void;
  closeGuide: () => void;
  replayTour: () => void;
  setStep: (step: Step) => void;
  setFile: (file: File | null, info?: VideoInfo | null, error?: string | null) => void;
  setRestorable: (p: Project | null) => void;
  restore: () => void;
  update: (patch: Partial<Project> | ((p: Project) => Partial<Project>)) => void;
  updateSettings: (patch: Partial<ExportSettings>) => void;
  updateEdits: (fn: (e: ClipEdits) => ClipEdits) => void;
}

export const fileKeyOf = (f: File) => `project:${f.name}:${f.size}:${f.lastModified}`;

export const useApp = create<AppState>((set, get) => ({
  step: 'start',
  file: null,
  url: null,
  fileKey: null,
  info: null,
  probeError: null,
  restorable: null,
  project: defaultProject(),
  exporting: false,
  view: typeof location !== 'undefined' && location.hash.startsWith('#guide') ? 'guide' : 'app',
  tourNonce: 0,
  openGuide: (section) => {
    set({ view: 'guide' });
    const hash = section ? `#guide-${section}` : '#guide';
    if (location.hash !== hash) history.pushState(null, '', hash);
    requestAnimationFrame(() => {
      if (section) document.getElementById(`guide-${section}`)?.scrollIntoView({ block: 'start' });
      else window.scrollTo({ top: 0 });
    });
  },
  closeGuide: () => {
    set({ view: 'app' });
    if (location.hash.startsWith('#guide')) history.pushState(null, '', location.pathname + location.search);
    window.scrollTo({ top: 0 });
  },
  replayTour: () => set((s) => ({ tourNonce: s.tourNonce + 1 })),
  setStep: (step) => {
    set({ step });
    window.scrollTo({ top: 0 });
  },
  setFile: (file, info = null, error = null) => {
    const old = get().url;
    if (old) URL.revokeObjectURL(old);
    set({
      file,
      url: file ? URL.createObjectURL(file) : null,
      fileKey: file ? fileKeyOf(file) : null,
      info,
      probeError: error,
      restorable: null,
      project: { ...defaultProject(), method: get().project.method },
    });
  },
  setRestorable: (p) => set({ restorable: p }),
  restore: () => {
    const r = get().restorable;
    if (r) set({ project: { ...defaultProject(), ...r }, restorable: null });
  },
  update: (patch) => set((s) => ({ project: { ...s.project, ...(typeof patch === 'function' ? patch(s.project) : patch) } })),
  updateSettings: (patch) => set((s) => ({ project: { ...s.project, settings: { ...s.project.settings, ...patch } } })),
  updateEdits: (fn) => set((s) => ({ project: { ...s.project, edits: fn(s.project.edits) } })),
}));
